import React, { useState } from "react";
import { MarketingNav, MarketingFooter, TMarketingChrome } from "../../components/Marketing/MarketingChrome/MarketingChrome";
import { formOperation } from "../../wrappers/formOperation";
import { PublicIntroducerSearchForm, TPublicIntroducerSearchResult } from "../../../../domain/codecs/form/PublicIntroducerSearchForm";
import { PublicPropertyReportAddressSearchForm, PublicPropertyReportCreateForm } from "../../../../domain/codecs/form/PublicPropertyReportForm";
import { TCallbackPreference1 } from "../../../../domain/codecs/CallbackPreference";
import { TTransactionType1 } from "../../../../domain/codecs/TransactionType";
import { FUNNEL_EVENT, LEAD_EVENT, trackEvent } from "../../marketing/analytics";

declare const grecaptcha: {
    ready: (cb: () => void) => void;
    execute: (publicKey: string, options: { action: string }) => Promise<string>;
};

type TAddressOption = {
    id: number;
    label: string;
    form: ReturnType<typeof PublicPropertyReportAddressSearchForm.newDefault>["children"]["results"][number];
};

type TTitleOption = {
    titleNumber: string;
    address: string;
};

type TStep = "address" | "pick" | "title" | "intent" | "details" | "submitting";

// Intent is the domain's `transaction_type`. Asked on its own one-tap step because it is the
// one answer that changes what gets built - the case type, how many quotes are generated, and
// the intent VAL receives - rather than just how we make the call.
const INTENT_OPTIONS: Array<{ value: TTransactionType1; label: string; note: string }> = [
    { value: "sale", label: "I'll be selling it", note: "The usual route once probate is under way" },
    { value: "transfer", label: "I'm transferring it to someone", note: "Keeping it in the family rather than selling" },
    { value: "unknown", label: "I haven't decided yet", note: "That's fine - the valuation helps you decide" },
];

// Bounded by the 8am-6pm hours the site already advertises. A day-part rather than a time,
// because we ring from a queue and cannot honour an appointment.
const CALLBACK_OPTIONS: Array<{ value: TCallbackPreference1; label: string }> = [
    { value: "as_soon_as_possible", label: "As soon as you can" },
    { value: "morning", label: "Morning" },
    { value: "afternoon", label: "Afternoon" },
    { value: "early_evening", label: "Early evening" },
];

const AMICABLE_INTRODUCER_ID = "43dceef9-8f60-40dd-aaf4-578661d8a588";

/**
 * reCAPTCHA v3, resolved lazily and defensively.
 *
 * The server verifies this token before it will spend £7 at the Land Registry, so a missing
 * script must fail closed with an explanation rather than silently posting an empty token
 * and surfacing a generic error.
 */
const recaptchaToken = (): Promise<string> =>
    new Promise((resolve, reject) => {
        if (typeof grecaptcha === "undefined") {
            reject(new Error("recaptcha-unavailable"));
            return;
        }
        grecaptcha.ready(() => {
            grecaptcha
                .execute(env.REACT_APP_RECAPTCHA_PUBLIC_KEY, { action: "CreatePublicPropertyReport" })
                .then(resolve)
                .catch(() => reject(new Error("recaptcha-failed")));
        });
    });

/**
 * The controller folds three distinct failures into `validationError`, and collapsing them
 * into one message cost us a live incident: a key mismatch read as "our systems are down"
 * for hours, when the user only needed to reload. Each cause gets its own next step.
 */
export const submitErrorMessage = (validationErrors: Array<[string, string]>): string => {
    const codes = validationErrors.map(([code]) => code);

    if (codes.includes("RecaptchaFailed")) {
        return "We couldn't verify your browser. Please reload the page and try again, or call us on 0808 196 5200 and we'll do this for you.";
    }
    if (codes.includes("ServiceUnavailable")) {
        return "The Land Registry isn't responding right now. Nothing has been charged - please try again shortly, or call us on 0808 196 5200.";
    }
    if (codes.includes("ReportUnavailable")) {
        return "We couldn't read the Land Registry record for this property. Nothing has been charged - please call us on 0808 196 5200 and we'll sort it manually.";
    }
    if (codes.includes("ContactDetailsRequired")) {
        return "We need an email address or a phone number before we can put the report together. Please add one above and try again.";
    }
    if (codes.includes("IntroducerNotFound")) {
        return "We couldn't recognise the referral link you arrived with. Nothing has been charged - please call us on 0808 196 5200 and we'll do this for you.";
    }
    return "Something went wrong at our end. Nothing has been charged or committed - please call us on 0808 196 5200 and we'll do this for you.";
};

const Field = (props: {
    id: string;
    label: string;
    value: string;
    type?: string;
    autoComplete?: string;
    hint?: string;
    onChange: (value: string) => void;
    // Rendered directly under the input, for a field that carries something extra - the
    // introducer type-ahead's suggestion list. Kept here rather than letting that field
    // hand-roll its own input, which is how it ended up half the height of every other
    // field on the step.
    children?: React.ReactNode;
}): JSX.Element =>
    <div className="funnel__field">
        <label className="funnel__label" htmlFor={props.id}>{props.label}</label>
        {props.hint ? <p className="funnel__hint">{props.hint}</p> : null}
        <input
            className="front-input-general front-input-general--text-align-left front-input-general--enabled front-input-general--no-error funnel__input"
            id={props.id}
            name={props.id}
            type={props.type || "text"}
            autoComplete={props.autoComplete}
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
        />
        {props.children}
    </div>;


// An unmatched name is still worth having - "my solicitor Dave at Dave & Co" is a lead
// someone can chase - but it is not an attribution, so it rides in the notes rather than
// silently becoming an introducer_id.
const composeAdminNotes = (
    notes: string,
    introducerQuery: string,
    chosen: TPublicIntroducerSearchResult | null,
): string => {
    const typed = introducerQuery.trim();
    const unmatched = chosen === null && typed !== ""
        ? `Said they were introduced by: ${typed}`
        : "";

    return [notes.trim(), unmatched].filter((line) => line !== "").join("\n");
};

export const ValuationFunnelContainer = (props: {
    view: TMarketingChrome;
    initialPostcode: string;
    introducerId: string | null;
}): JSX.Element => {
    const [step, setStep] = useState<TStep>("address");
    const [postcode, setPostcode] = useState(props.initialPostcode);
    const [buildingNumberName, setBuildingNumberName] = useState("");
    const [options, setOptions] = useState<Array<TAddressOption>>([]);
    const [chosen, setChosen] = useState<TAddressOption | null>(null);
    const [titleNumber, setTitleNumber] = useState<string>("");
    const [titleOptions, setTitleOptions] = useState<Array<TTitleOption>>([]);
    const [transactionType, setTransactionType] = useState<TTransactionType1 | null>(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [callbackPreference, setCallbackPreference] = useState<TCallbackPreference1 | null>(null);
    const [adminNotes, setAdminNotes] = useState("");
    // Only ever shown when the visitor did NOT arrive on a partner link. Asking someone who
    // followed amicable's own link who referred them is a visible failure.
    const [introducerQuery, setIntroducerQuery] = useState("");
    const [introducerResults, setIntroducerResults] = useState<Array<TPublicIntroducerSearchResult>>([]);
    const [chosenIntroducer, setChosenIntroducer] = useState<TPublicIntroducerSearchResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const askWhoIntroducedThem = props.introducerId === null;

    // We do not ring leads from Amicable's link, so this gates the preference question as
    // well as the copy - callback_preference goes up null rather than defaulting to a slot.
    const promisesACall = props.introducerId !== AMICABLE_INTRODUCER_ID;

    // The id the listing ends up attributed to: the link wins, then whatever the visitor
    // picked. A typed name that was never matched to a partner stays in the notes.
    const introducerIdForSubmit = props.introducerId ?? chosenIntroducer?.id ?? null;

    const searchIntroducers = async (raw: string): Promise<void> => {
        setIntroducerQuery(raw);
        setChosenIntroducer(null);

        if (raw.trim().length < 2) {
            setIntroducerResults([]);
            return;
        }

        const blank = PublicIntroducerSearchForm.newDefault();
        const response = await formOperation("GetPublicIntroducerSearch", {
            ...blank,
            edited: { ...blank.edited, query: raw.trim() },
        });

        setIntroducerResults(response.status === "success" ? response.children.results : []);
    };

    const searchAddresses = async (): Promise<void> => {
        setError(null);

        if (postcode.trim() === "") {
            setError("Please enter the postcode of the property.");
            return;
        }

        const blank = PublicPropertyReportAddressSearchForm.newDefault();
        const response = await formOperation("GetPublicPropertyReportAddressSearch", {
            ...blank,
            edited: {
                ...blank.edited,
                postcode: postcode.trim(),
                building_number_name: buildingNumberName.trim(),
                wants_valuation: true,
                introducer_id: props.introducerId,
            },
        });

        if (response.status !== "success") {
            setError("We couldn't find that postcode. Check it and try again, or call us on 0808 196 5200 and we'll do it for you.");
            return;
        }

        const seen = new Set<string>();
        const results = response.children.results
            .map((form, i) => ({
                id: form.edited.id || i + 1,
                label: form.children.address_string,
                form,
            }))
            .filter((option) => {
                if (seen.has(option.label)) {
                    return false;
                }
                seen.add(option.label);
                return true;
            });

        if (results.length === 0) {
            setError("We couldn't find any addresses at that postcode. Call us on 0808 196 5200 and we'll sort it out.");
            return;
        }

        setOptions(results);
        trackEvent(FUNNEL_EVENT.started, { address_results: results.length });
        setStep("pick");
    };

    const chooseAddress = async (option: TAddressOption): Promise<void> => {
        setError(null);
        setChosen(option);

        const response = await formOperation("GetPublicPropertyReportTitleNumbers", option.form);
        const titles = (response.children.response?._tag === "success"
            ? response.children.response.results
            : [])
            .filter((title) => title.edited.title_number !== "")
            .map((title): TTitleOption => ({
                titleNumber: title.edited.title_number,
                address: title.children.read_only.full_address,
            }));

        setTitleOptions(titles);

        setTitleNumber(titles.length === 1 ? titles[0].titleNumber : "");
        trackEvent(FUNNEL_EVENT.addressChosen, { title_options: titles.length });
        setStep(titles.length > 1 ? "title" : "intent");
    };

    const submit = async (): Promise<void> => {
        setError(null);

        if (firstName.trim() === "" || lastName.trim() === "") {
            setError("Please tell us your name so we know who we're speaking to.");
            trackEvent(FUNNEL_EVENT.blocked, { reason: "name_missing" });
            return;
        }
        if (email.trim() === "") {
            setError("Please give us an email address - that's where the written valuation goes once we've spoken.");
            trackEvent(FUNNEL_EVENT.blocked, { reason: "email_missing" });
            return;
        }
        // Required on both paths: on the call path we would otherwise hold a lead we have
        // promised to ring and no way to ring it, and off it we still need a way to reach them.
        if (phone.trim() === "") {
            setError(promisesACall
                ? "Please give us a phone number - the valuation is talked through on a call."
                : "Please give us a phone number, in case we need to check something about the property.");
            trackEvent(FUNNEL_EVENT.blocked, { reason: "phone_missing" });
            return;
        }
        if (promisesACall && callbackPreference === null) {
            setError("Please tell us when suits you for that call.");
            trackEvent(FUNNEL_EVENT.blocked, { reason: "callback_missing" });
            return;
        }
        if (chosen === null) {
            setError("Please choose the property first.");
            return;
        }

        setStep("submitting");

        let token = "";
        try {
            token = await recaptchaToken();
        } catch {
            setError("We couldn't verify your browser. Please reload and try again, or call us on 0808 196 5200.");
            trackEvent(FUNNEL_EVENT.submitFailed, { reason: "recaptcha_unavailable" });
            setStep("details");
            return;
        }

        const blank = PublicPropertyReportCreateForm.newDefault();
        const response = await formOperation("CreatePublicPropertyReport", {
            ...blank,
            edited: {
                ...blank.edited,
                ...chosen.form.edited,
                title_number: titleNumber,
                recaptcha_token: token,
                wants_valuation: true,
                introducer_id: introducerIdForSubmit,
                transaction_type: transactionType,
                callback_preference: callbackPreference,
                admin_notes: composeAdminNotes(adminNotes, introducerQuery, chosenIntroducer),
                executor_or_beneficiary: {
                    first_name: firstName.trim(),
                    last_name: lastName.trim(),
                    email: email.trim(),
                    phone_number: phone.trim(),
                    is_executor: false,
                },
            },
        });

        if (response.status !== "success") {
            setError(submitErrorMessage(response.validationErrors));
            trackEvent(FUNNEL_EVENT.submitFailed, {
                reason: response.validationErrors.map(([code]) => code).join(",") || "unknown",
            });
            setStep("details");
            return;
        }

        // The conversion. Fired before the redirect - gtag sends via navigator.sendBeacon,
        // which survives the navigation, so it does not need the page to stay alive.
        trackEvent(LEAD_EVENT, {
            intent: transactionType || "unknown",
            callback_preference: callbackPreference,
            has_notes: adminNotes.trim() !== "",
        });

        window.location.href = promisesACall ? "/valuation/complete" : "/valuation/complete/in-writing";
    };

    return (
        <div className="marketing funnel">
            <MarketingNav view={props.view} />

            <main className="marketing__main">
                <section className="marketing__section marketing__section--bare marketing__section--pageHeader">
                    <div className="marketing__container">
                        <div>
                            <p className="marketing__eyebrow">Free valuation</p>
                            <h1 className="marketing__pageTitle">
                                {step === "intent" ? "What are you planning to do with it?"
                                    : step === "details" || step === "submitting" ? "How shall we reach you?"
                                    : "Which property is it?"}
                            </h1>
                            <p className="marketing__pageIntro">
                                {step === "intent"
                                    ? "It changes what we look at, and there's no wrong answer."
                                    : step === "details" || step === "submitting"
                                    ? promisesACall
                                        ? "Last step. We'll ring you to talk the valuation through, at a time you choose."
                                        : "Last step. We'll put the valuation together and send it to you in writing."
                                    : "Start with the postcode. It takes about two minutes and there's nothing to arrange today."}
                            </p>
                        </div>
                    </div>
                </section>

                <section className="marketing__section marketing__section--bare">
                    <div className="marketing__container funnel__layout">
                        <div className="funnel__form">
                            <ol className="funnel__steps">
                                <li className={`funnel__stepMarker${step === "address" ? " funnel__stepMarker--on" : ""}`}>Property</li>
                                <li className={`funnel__stepMarker${step === "pick" || step === "title" ? " funnel__stepMarker--on" : ""}`}>Address</li>
                                <li className={`funnel__stepMarker${step === "intent" ? " funnel__stepMarker--on" : ""}`}>Plans</li>
                                <li className={`funnel__stepMarker${step === "details" || step === "submitting" ? " funnel__stepMarker--on" : ""}`}>Details</li>
                            </ol>

                            {error === null ? null : <p className="funnel__error" role="alert">{error}</p>}

                            {step === "address"
                                ? <div>
                                    <Field
                                        id="postcode"
                                        label="Postcode"
                                        value={postcode}
                                        autoComplete="postal-code"
                                        onChange={setPostcode}
                                    />
                                    <Field
                                        id="building_number_name"
                                        label="House number or name"
                                        hint="Optional - it just narrows the list down."
                                        value={buildingNumberName}
                                        onChange={setBuildingNumberName}
                                    />
                                    <button className="funnel__submit" type="button" onClick={searchAddresses}>
                                        Find the property
                                    </button>
                                </div>
                                : null}

                            {step === "pick"
                                ? <div>
                                    <ul className="funnel__options">
                                        {options.map((option) =>
                                            <li key={option.id}>
                                                <button
                                                    className="funnel__option"
                                                    type="button"
                                                    onClick={() => chooseAddress(option)}
                                                >
                                                    {option.label}
                                                </button>
                                            </li>,
                                        )}
                                    </ul>
                                    <button className="funnel__back" type="button" onClick={() => setStep("address")}>
                                        Not there? Search again
                                    </button>
                                </div>
                                : null}

                            {step === "title"
                                ? <div>
                                    <p className="funnel__chosen">{chosen === null ? "" : chosen.label}</p>
                                    <p className="funnel__hint">
                                        This address covers more than one registered title. Which one is the property you're selling?
                                    </p>
                                    <ul className="funnel__options">
                                        {titleOptions.map((option) =>
                                            <li key={option.titleNumber}>
                                                <button
                                                    className="funnel__option"
                                                    type="button"
                                                    onClick={() => { setTitleNumber(option.titleNumber); setStep("intent"); }}
                                                >
                                                    {option.address}
                                                    <span className="funnel__optionNote">Title {option.titleNumber}</span>
                                                </button>
                                            </li>,
                                        )}
                                    </ul>
                                    <button className="funnel__back" type="button" onClick={() => setStep("pick")}>
                                        Choose a different address
                                    </button>
                                </div>
                                : null}

                            {step === "intent"
                                ? <div>
                                    {chosen === null ? null : <p className="funnel__chosen">{chosen.label}</p>}
                                    <ul className="funnel__options">
                                        {INTENT_OPTIONS.map((option) =>
                                            <li key={option.value}>
                                                <button
                                                    className="funnel__option"
                                                    type="button"
                                                    onClick={() => { setTransactionType(option.value); trackEvent(FUNNEL_EVENT.intentChosen, { intent: option.value }); setStep("details"); }}
                                                >
                                                    {option.label}
                                                    <span className="funnel__optionNote">{option.note}</span>
                                                </button>
                                            </li>,
                                        )}
                                    </ul>
                                    <button
                                        className="funnel__back"
                                        type="button"
                                        onClick={() => setStep(titleOptions.length > 1 ? "title" : "pick")}
                                    >
                                        Back
                                    </button>
                                </div>
                                : null}

                            {step === "details" || step === "submitting"
                                ? <div>
                                    {chosen === null ? null : <p className="funnel__chosen">{chosen.label}</p>}
                                    <Field id="first_name" label="First name" value={firstName} autoComplete="given-name" onChange={setFirstName} />
                                    <Field id="last_name" label="Last name" value={lastName} autoComplete="family-name" onChange={setLastName} />
                                    <Field id="email" label="Email" type="email" value={email} autoComplete="email" onChange={setEmail} />
                                    <Field
                                        id="phone_number"
                                        label="Phone number"
                                        hint={promisesACall
                                            ? "We talk the valuation through on the phone, so we do need this one."
                                            : "In case we need to check something about the property."}
                                        type="tel"
                                        value={phone}
                                        autoComplete="tel"
                                        onChange={setPhone}
                                    />

                                    {promisesACall
                                        ? <fieldset className="funnel__fieldset">
                                            <legend className="funnel__label">When suits you for that call?</legend>
                                            <p className="funnel__hint">We're open 8am to 6pm, Monday to Friday.</p>
                                            <ul className="funnel__options">
                                                {CALLBACK_OPTIONS.map((option) =>
                                                    <li key={option.value}>
                                                        <label
                                                            className={`funnel__option${callbackPreference === option.value ? " funnel__option--on" : ""}`}
                                                        >
                                                            <input
                                                                className="funnel__radio"
                                                                type="radio"
                                                                name="callback_preference"
                                                                value={option.value}
                                                                checked={callbackPreference === option.value}
                                                                onChange={() => setCallbackPreference(option.value)}
                                                            />
                                                            {option.label}
                                                        </label>
                                                    </li>,
                                                )}
                                            </ul>
                                        </fieldset>
                                        : null}

                                    {askWhoIntroducedThem
                                        ? <Field
                                            id="introduced_by"
                                            label="Who introduced you to us?"
                                            hint="Optional. Start typing and pick them from the list, so we can tell them we are looking after you."
                                            autoComplete="off"
                                            value={chosenIntroducer ? chosenIntroducer.name : introducerQuery}
                                            onChange={searchIntroducers}
                                        >
                                            {chosenIntroducer === null && introducerResults.length > 0
                                                ? <ul className="funnel__suggestions">
                                                    {introducerResults.map((result) =>
                                                        <li key={result.id}>
                                                            <button
                                                                className="funnel__suggestion"
                                                                type="button"
                                                                onClick={() => {
                                                                    setChosenIntroducer(result);
                                                                    setIntroducerResults([]);
                                                                }}
                                                            >
                                                                {result.name}
                                                            </button>
                                                        </li>,
                                                    )}
                                                </ul>
                                                : null}
                                        </Field>
                                        : null}

                                    <div className="funnel__field">
                                        <label className="funnel__label" htmlFor="admin_notes">
                                            {promisesACall ? "Anything we should know before we call?" : "Anything we should know?"}
                                        </label>
                                        <p className="funnel__hint">
                                            Optional. Anything at all - who else is involved, whether someone's still living there, what state it's in.
                                        </p>
                                        <textarea
                                            className="funnel__textarea"
                                            id="admin_notes"
                                            name="admin_notes"
                                            maxLength={1000}
                                            rows={4}
                                            value={adminNotes}
                                            onChange={(e) => setAdminNotes(e.target.value)}
                                        />
                                    </div>

                                    <button
                                        className="funnel__submit"
                                        type="button"
                                        disabled={step === "submitting"}
                                        onClick={submit}
                                    >
                                        {step === "submitting" ? "Sending..." : "Request my valuation"}
                                    </button>
                                    <p className="funnel__smallprint">
                                        Free, and yours to keep whether or not you ever sell with us.
                                    </p>
                                </div>
                                : null}
                        </div>

                        {/* The right-hand column exists because the card was 1040px wide
                            with a 300px form marooned in the left third of it, and because
                            this is the exact moment someone decides whether to hand over
                            their details — the page they arrived from carried the proof and
                            the promises, and this one carried none of them. */}
                        <aside className="funnel__aside">
                            <p className="funnel__asideTitle">What happens next</p>
                            <ol className="funnel__asideSteps">
                                <li>We check the property against the Land Registry and recent local sales.</li>
                                {promisesACall
                                    ? <li>We ring you at the time you picked and talk the valuation through.</li>
                                    : null}
                                <li>You get it in writing, in the format the grant application needs.</li>
                            </ol>

                            <p className="funnel__asideProof">
                                <span className="funnel__asideRating">4.8 out of 5</span>
                                <span> from 56 reviews on Trustpilot</span>
                            </p>

                            <p className="funnel__asideHelp">
                                Stuck, or would you rather just talk to someone?
                                <a className="funnel__asidePhone" href={props.view.phoneUrl}>{props.view.phoneText}</a>
                            </p>
                        </aside>
                    </div>
                </section>
            </main>

            <MarketingFooter view={props.view} />
        </div>
    );
};
